import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { buildPath, displayValue, isNumericToggleable } from './utils';

interface JsonNodeProps {
	value: unknown;
	path: string;
	depth: number;
	keyName?: string;
	keyEditable: boolean;
	isExpanded: boolean;
	isFocused: boolean;
	sortAlpha: boolean;
	readOnly: boolean;
	expandedPaths: Set<string>;
	editingPath: string | null;
	editingTarget: 'key' | 'value';
	focusedPath: string | null;
	onToggleExpand: (path: string) => void;
	onStartEdit: (path: string, target: 'key' | 'value') => void;
	onCommitEdit: (path: string, raw: string) => void;
	onRenameKey: (path: string, newKey: string) => void;
	onCommitAndTab: (
		path: string,
		target: 'key' | 'value',
		raw: string,
		direction: 'forward' | 'backward',
	) => void;
	onCancelEdit: () => void;
	onDelete: (path: string) => void;
	onToggleNumeric: (path: string, value: unknown) => void;
	onAddKey: (path: string, key: string) => void;
	onAddElement: (path: string) => void;
}

const INDENT_PX = 20;

function isExpandable(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	return typeof value === 'object';
}

function getPreview(value: unknown): string {
	if (Array.isArray(value)) {
		return value.length === 0 ? '[]' : `[${value.length} items]`;
	}
	if (typeof value === 'object' && value !== null) {
		const keys = Object.keys(value as Record<string, unknown>);
		return keys.length === 0 ? '{}' : `{${keys.length} keys}`;
	}
	return '';
}

function ValueColor({ value }: { value: unknown }) {
	if (value === null || value === undefined) {
		return <span className="text-gray-400">{String(value)}</span>;
	}
	if (typeof value === 'boolean') {
		return <span className="text-purple-600">{String(value)}</span>;
	}
	if (typeof value === 'number') {
		return <span className="text-blue-600">{value}</span>;
	}
	if (typeof value === 'string') {
		return <span className="text-green-600">&quot;{value}&quot;</span>;
	}
	return <span>{String(value)}</span>;
}

function InlineInput({
	initialValue,
	onCommit,
	onCancel,
	onTab,
	className,
}: {
	initialValue: string;
	onCommit: (raw: string) => void;
	onCancel: () => void;
	onTab: (raw: string, direction: 'forward' | 'backward') => void;
	className?: string;
}) {
	const [raw, setRaw] = useState(initialValue);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onCommit(raw);
		} else if (e.key === 'Escape') {
			e.preventDefault();
			onCancel();
		} else if (e.key === 'Tab') {
			e.preventDefault();
			onTab(raw, e.shiftKey ? 'backward' : 'forward');
		}
	};

	return (
		<input
			ref={inputRef}
			className={
				className ??
				`rounded border border-blue-400 bg-white px-1 font-mono text-sm
				text-gray-900 outline-none focus:ring-1 focus:ring-blue-400`
			}
			style={{ width: `${Math.max(raw.length + 2, 4)}ch` }}
			value={raw}
			onChange={(e) => setRaw(e.target.value)}
			onKeyDown={handleKeyDown}
			onBlur={() => onCommit(raw)}
		/>
	);
}

function AddKeyInput({
	onCommit,
	onCommitAndEditValue,
	onCancel,
}: {
	onCommit: (key: string) => void;
	onCommitAndEditValue: (key: string) => void;
	onCancel: () => void;
}) {
	const [keyName, setKeyName] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (keyName.trim()) onCommit(keyName.trim());
		} else if (e.key === 'Tab') {
			e.preventDefault();
			if (keyName.trim()) onCommitAndEditValue(keyName.trim());
		} else if (e.key === 'Escape') {
			e.preventDefault();
			onCancel();
		}
	};

	return (
		<span className="inline-flex items-center gap-1">
			<input
				ref={inputRef}
				className="rounded border border-blue-400 bg-white px-1 font-mono
					text-sm text-gray-700 outline-none focus:ring-1 focus:ring-blue-400"
				style={{ width: `${Math.max(keyName.length + 2, 8)}ch` }}
				placeholder="key name"
				value={keyName}
				onChange={(e) => setKeyName(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={() => {
					if (keyName.trim()) onCommit(keyName.trim());
					else onCancel();
				}}
			/>
			<span className="text-xs text-gray-400">: null</span>
		</span>
	);
}

export const JsonNode = memo(function JsonNode({
	value,
	path,
	depth,
	keyName,
	keyEditable,
	isExpanded,
	isFocused,
	sortAlpha,
	readOnly,
	expandedPaths,
	editingPath,
	editingTarget,
	focusedPath,
	onToggleExpand,
	onStartEdit,
	onCommitEdit,
	onRenameKey,
	onCommitAndTab,
	onCancelEdit,
	onDelete,
	onToggleNumeric,
	onAddKey,
	onAddElement,
}: JsonNodeProps) {
	const [addingKey, setAddingKey] = useState(false);
	const [copied, setCopied] = useState(false);
	const nodeRef = useRef<HTMLDivElement>(null);

	const isEditingKey = editingPath === path && editingTarget === 'key';
	const isEditingValue = editingPath === path && editingTarget === 'value';

	useEffect(() => {
		if (isFocused && nodeRef.current) {
			nodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [isFocused]);

	const handleToggle = useCallback(() => {
		onToggleExpand(path);
	}, [onToggleExpand, path]);

	const handleValueClick = useCallback(() => {
		if (!readOnly) onStartEdit(path, 'value');
	}, [onStartEdit, path, readOnly]);

	const handleKeyClick = useCallback(() => {
		if (keyEditable && !readOnly) {
			onStartEdit(path, 'key');
		}
	}, [keyEditable, readOnly, onStartEdit, path]);

	const handleValueCommit = useCallback(
		(raw: string) => onCommitEdit(path, raw),
		[onCommitEdit, path],
	);

	const handleKeyCommit = useCallback(
		(newKey: string) => onRenameKey(path, newKey),
		[onRenameKey, path],
	);

	const handleValueTab = useCallback(
		(raw: string, direction: 'forward' | 'backward') =>
			onCommitAndTab(path, 'value', raw, direction),
		[onCommitAndTab, path],
	);

	const handleKeyTab = useCallback(
		(raw: string, direction: 'forward' | 'backward') =>
			onCommitAndTab(path, 'key', raw, direction),
		[onCommitAndTab, path],
	);

	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDelete(path);
		},
		[onDelete, path],
	);

	const handleToggleNumeric = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onToggleNumeric(path, value);
		},
		[onToggleNumeric, path, value],
	);

	const expandable = isExpandable(value);

	const handleAddKey = useCallback(
		(key: string) => {
			onAddKey(path, key);
			setAddingKey(false);
		},
		[onAddKey, path],
	);

	const handleAddKeyAndEditValue = useCallback(
		(key: string) => {
			onAddKey(path, key);
			setAddingKey(false);
			const newPath = path ? `${path}.${key}` : key;
			onStartEdit(newPath, 'value');
		},
		[onAddKey, onStartEdit, path],
	);

	const handleAddElement = useCallback(() => {
		onAddElement(path);
	}, [onAddElement, path]);

	const handleCopy = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			const text =
				typeof value === 'string' ? value : JSON.stringify(value, null, 2);
			navigator.clipboard.writeText(text).then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			});
		},
		[value],
	);

	const childProps = {
		sortAlpha,
		readOnly,
		expandedPaths,
		editingPath,
		editingTarget,
		focusedPath,
		onToggleExpand,
		onStartEdit,
		onCommitEdit,
		onRenameKey,
		onCommitAndTab,
		onCancelEdit,
		onDelete,
		onToggleNumeric,
		onAddKey,
		onAddElement,
	};

	const renderChildren = () => {
		if (!expandable || !isExpanded) return null;
		const isArray = Array.isArray(value);

		const children = isArray
			? (value as unknown[]).map((item, index) => {
					const childPath = buildPath(path, index);
					return (
						<JsonNode
							key={childPath}
							value={item}
							path={childPath}
							depth={depth + 1}
							keyName={String(index)}
							keyEditable={false}
							isExpanded={expandedPaths.has(childPath)}
							isFocused={focusedPath === childPath}
							{...childProps}
						/>
					);
				})
			: (() => {
					const entries = Object.entries(value as Record<string, unknown>);
					const sorted = sortAlpha
						? [...entries].sort(([a], [b]) => a.localeCompare(b))
						: entries;
					return sorted.map(([key, val]) => {
						const childPath = buildPath(path, key);
						return (
							<JsonNode
								key={childPath}
								value={val}
								path={childPath}
								depth={depth + 1}
								keyName={key}
								keyEditable={true}
								isExpanded={expandedPaths.has(childPath)}
								isFocused={focusedPath === childPath}
								{...childProps}
							/>
						);
					});
				})();

		return (
			<>
				{children}
				{!readOnly && (
					<div
						className="flex items-center gap-1 py-px font-mono text-sm"
						style={{ paddingLeft: `${(depth + 1) * INDENT_PX}px` }}
					>
						<span className="w-4 shrink-0" />
						{addingKey ? (
							<AddKeyInput
								onCommit={handleAddKey}
								onCommitAndEditValue={handleAddKeyAndEditValue}
								onCancel={() => setAddingKey(false)}
							/>
						) : (
							<button
								className="rounded px-1 text-xs text-gray-400 hover:bg-gray-100
									hover:text-gray-600"
								onClick={isArray ? handleAddElement : () => setAddingKey(true)}
								title={isArray ? 'Add element' : 'Add key'}
							>
								+
							</button>
						)}
					</div>
				)}
			</>
		);
	};

	const focusHighlight = isFocused
		? 'rounded bg-yellow-50 ring-1 ring-yellow-300'
		: '';

	const canEditKey = keyEditable && !readOnly;

	return (
		<div ref={nodeRef}>
			<div
				className={`group flex items-center gap-1 py-px font-mono text-sm
					hover:bg-gray-50 ${focusHighlight}`}
				style={{ paddingLeft: `${depth * INDENT_PX}px` }}
			>
				{/* Expand/collapse arrow */}
				{expandable ? (
					<button
						className="flex w-4 shrink-0 items-center justify-center
							text-gray-400 hover:text-gray-600"
						onClick={handleToggle}
					>
						{isExpanded ? '▼' : '▶'}
					</button>
				) : (
					<span className="w-4 shrink-0" />
				)}

				{/* Key name */}
				{keyName !== undefined &&
					(isEditingKey ? (
						<>
							<InlineInput
								initialValue={keyName}
								onCommit={handleKeyCommit}
								onCancel={onCancelEdit}
								onTab={handleKeyTab}
								className="rounded border border-blue-400 bg-white px-1
									font-mono text-sm text-gray-700 outline-none focus:ring-1
									focus:ring-blue-400"
							/>
							<span className="text-gray-700">:&nbsp;</span>
						</>
					) : (
						<span
							className={`shrink-0 text-gray-700
								${canEditKey ? 'cursor-pointer hover:underline' : ''}`}
							onClick={handleKeyClick}
						>
							{keyName}:&nbsp;
						</span>
					))}

				{/* Value */}
				{expandable ? (
					<span className="cursor-pointer text-gray-400" onClick={handleToggle}>
						{isExpanded
							? Array.isArray(value)
								? '['
								: '{'
							: getPreview(value)}
					</span>
				) : isEditingValue ? (
					<InlineInput
						initialValue={displayValue(value)}
						onCommit={handleValueCommit}
						onCancel={onCancelEdit}
						onTab={handleValueTab}
					/>
				) : (
					<span
						className={readOnly ? '' : 'cursor-pointer hover:underline'}
						onClick={handleValueClick}
					>
						<ValueColor value={value} />
					</span>
				)}

				{/* Action buttons (hover-visible) */}
				<span
					className="ml-1 flex shrink-0 items-center gap-0.5 opacity-0
						transition-opacity group-hover:opacity-100"
				>
					<button
						className={`rounded px-1 text-xs ${
							copied
								? 'text-green-600'
								: 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
							}`}
						onClick={handleCopy}
						title="Copy JSON"
					>
						{copied ? '✓' : '⧉'}
					</button>
					{!readOnly &&
						!expandable &&
						isNumericToggleable(value) &&
						!isEditingValue && (
							<button
								className="rounded px-1 text-xs text-gray-400 hover:bg-gray-100
									hover:text-gray-600"
								onClick={handleToggleNumeric}
								title={
									typeof value === 'number'
										? 'Convert to string'
										: 'Convert to number'
								}
							>
								{typeof value === 'number' ? 'Abc' : '#'}
							</button>
						)}
					{!readOnly && (
						<button
							className="rounded px-1 text-xs text-red-400 hover:bg-red-50
								hover:text-red-500"
							onClick={handleDelete}
							title="Delete"
						>
							×
						</button>
					)}
				</span>
			</div>

			{/* Children */}
			{renderChildren()}

			{/* Closing bracket */}
			{expandable && isExpanded && (
				<div
					className="py-px font-mono text-sm text-gray-400"
					style={{ paddingLeft: `${depth * INDENT_PX}px` }}
				>
					<span className="inline-block w-4 shrink-0" />
					{Array.isArray(value) ? ']' : '}'}
				</div>
			)}
		</div>
	);
});
